import { useState, useEffect, useCallback, useRef } from 'react';
import { userAPI } from '../services/api';
import { useToast } from '../contexts/ToastContext';

/**
 * Custom hook for managing user data fetching and state
 */
export const useUserData = () => {
  const { showError } = useToast();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [pagination, setPagination] = useState({ 
    page: 1, 
    limit: 10, 
    total: 0, 
    pages: 0 
  });
  const [filters, setFilters] = useState({
    status: ''
  });
  const isInitialMount = useRef(true);
  const previousParams = useRef({ page: 1, limit: 10, searchTerm: '', status: '' });

  /**
   * Helper function to extract error message from API error
   */
  const getErrorMessage = useCallback((err, defaultMessage) => {
    return err.data?.message || err.message || defaultMessage;
  }, []);

  /**
   * Load users from API
   */
  const loadUsers = useCallback(async (paramsOverride = null) => {
    const currentPage = paramsOverride?.page ?? pagination.page;
    const currentLimit = paramsOverride?.limit ?? pagination.limit;
    const currentSearch = paramsOverride?.searchTerm ?? searchTerm;
    const currentStatus = paramsOverride?.status ?? filters.status;
    
    try {
      setLoading(true);
      const params = {
        page: currentPage,
        limit: currentLimit,
        ...(currentSearch && { search: currentSearch }),
        ...(currentStatus && { status: currentStatus }),
      };

      const response = await userAPI.getAll(params);
      setUsers(response.users);
      
      // Update pagination with response data
      setPagination(prev => {
        const newPagination = response.pagination || {};
        // Preserve page and limit if they match what we requested
        return {
          page: newPagination.page ?? prev.page,
          limit: newPagination.limit ?? prev.limit,
          total: newPagination.total ?? prev.total,
          pages: newPagination.pages ?? prev.pages
        };
      });

      // Update previous params to track what we just loaded
      previousParams.current = {
        page: currentPage,
        limit: currentLimit,
        searchTerm: currentSearch,
        status: currentStatus
      };
      
      setError(null);
    } catch (err) {
      setError(err.message);
      const errorMessage = getErrorMessage(err, 'Failed to load users');
      showError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, searchTerm, filters.status, getErrorMessage, showError]);

  /**
   * Handle filter changes
   */
  const handleFilterChange = useCallback((filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
    // Reset to first page when filters change
    setPagination(prev => ({
      ...prev,
      page: 1
    }));
  }, []);

  /**
   * Clear all filters
   */
  const clearFilters = useCallback(() => {
    setFilters({
      status: ''
    });
    setSearchTerm('');
    setPagination(prev => ({
      ...prev,
      page: 1
    }));
  }, []);

  /**
   * Handle pagination changes
   * @param {number} newPage - New page number (defensive: validated to be positive integer)
   */
  const handlePageChange = useCallback((newPage) => {
    // Defensive: validate page number is positive integer
    const safePage = Math.max(1, Math.floor(newPage || 1));
    setPagination(prev => ({
      ...prev,
      page: safePage
    }));
  }, []);

  /**
   * Handle page size changes
   * @param {number} newLimit - New page size (defensive: validated to be positive integer)
   */
  const handlePageSizeChange = useCallback((newLimit) => {
    // Defensive: validate limit is positive integer
    const safeLimit = Math.max(1, Math.floor(newLimit || 10));
    setPagination(prev => ({
      ...prev,
      limit: safeLimit,
      page: 1 // Reset to first page when changing page size
    }));
  }, []);

  // Load users when search term, filters, or pagination changes
  useEffect(() => {
    if (isInitialMount.current) {
      // Initial load - call immediately without debounce
      isInitialMount.current = false;
      loadUsers();
      return;
    }

    // Check if any of the tracked parameters actually changed
    const paramsChanged = 
      previousParams.current.page !== pagination.page ||
      previousParams.current.limit !== pagination.limit ||
      previousParams.current.searchTerm !== searchTerm ||
      previousParams.current.status !== filters.status;

    // Only load if parameters actually changed (not from API response update)
    if (!paramsChanged) {
      return;
    }

    // Subsequent changes - debounce search
    const timeoutId = setTimeout(() => {
      loadUsers();
    }, 200); // Debounce search

    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, filters.status, pagination.page, pagination.limit]);

  return {
    users,
    loading,
    error,
    searchTerm,
    setSearchTerm,
    pagination,
    filters,
    setPagination,
    loadUsers,
    handleFilterChange,
    clearFilters,
    handlePageChange,
    handlePageSizeChange,
    setError,
    getErrorMessage
  };
};

